import React, { useRef, useState, useEffect, useMemo } from 'react';
import { ResourceManager } from './components/ResourceManager';
import ImageUploadGallery from '@/components/ImageUploadGallery';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';
import { supabase } from '@/lib/supabaseClient';
import { Download, Upload } from 'lucide-react';

const BoardMembersModule = () => {
  const { records, refresh } = useSupabaseResource('team');
  const [uploading, setUploading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [boardRoles, setBoardRoles] = useState([]);
  const fileInputRef = useRef();
  const defaultValues = useMemo(() => ({
    name: '',
    role_id: boardRoles[0]?.id || '',
    images: [],
    bio: '',
    display_order: 0,
    is_active: true,
  }), [boardRoles]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    const { data, error } = await supabase
      .from('team_roles')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      setRoles(data || []);
      // Filter only board roles
      const boardOnly = (data || []).filter(role => role.is_board_role === true);
      setBoardRoles(boardOnly);
    }
  };

  // Filter records to only show board members
  const filteredRecords = records.filter(record => {
    const role = roles.find(r => r.id === record.role_id);
    return role && role.is_board_role === true;
  });

  const exportToCSV = () => {
    const headers = ['name', 'role_id', 'image', 'bio', 'display_order', 'is_active'];
    const csvContent = [
      headers.join(','),
      ...filteredRecords.map(record => headers.map(h => {
        if (h === 'role_id') {
          const role = roles.find(r => r.id === record[h]);
          return `"${role ? role.name : record[h] || ''}"`;
        }
        return `"${record[h] || ''}"`;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'board_members.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        alert('CSV must have headers and at least one row');
        return;
      }
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const requiredHeaders = ['name', 'role'];
      const missing = requiredHeaders.filter(h => !headers.includes(h));
      if (missing.length) {
        alert(`Missing required headers: ${missing.join(', ')}`);
        return;
      }
      const team = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.replace(/"/g, '').trim());
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i] || '';
        });
        // map role name to id and ensure it's a board role
        const role = boardRoles.find(r => r.name.toLowerCase() === obj.role.toLowerCase());
        if (!role) {
          throw new Error(`Role "${obj.role}" is not a board role. Board roles must be marked as board roles in the Team Roles section.`);
        }
        obj.role_id = role.id;
        delete obj.role; // remove the old role field
        // transform
        obj.display_order = Number(obj.display_order) || 0;
        obj.is_active = obj.is_active !== 'false';
        return obj;
      });
      const { error } = await supabase.from('team').insert(team);
      if (error) throw error;
      alert('Bulk upload successful');
      refresh();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      {boardRoles.length === 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No board roles are active. Add or activate board roles in the Team Roles section to create board members.
        </div>
      )}
      <ResourceManager
        table="team"
        title="Board Members"
        description="Manage board members and directors. Only shows team members with board roles."
        columns={[
          {
            label: 'Image',
            accessor: 'image',
            render: (value) => value ? (
              <img src={value} alt="Board member" className="w-12 h-12 object-cover rounded" />
            ) : 'No image'
          },
          { label: 'Name', accessor: 'name' },
          {
            label: 'Board Role',
            accessor: 'role_id',
            render: (value) => {
              const role = roles.find(r => r.id === value);
              return role ? role.name : 'Unknown';
            }
          },
          { label: 'Order', accessor: 'display_order' },
          {
            label: 'Active',
            accessor: 'is_active',
            render: (value) => value ? 'Yes' : 'No'
          },
        ]}
        formFields={[
          { name: 'name', label: 'Name', placeholder: 'Board Member Name' },
          { 
            name: 'role_id', 
            label: 'Board Role', 
            type: 'select', 
            options: boardRoles.map(role => ({
              label: role.name,
              value: role.id
            }))
          },
          { name: 'images', label: 'Profile Image', type: 'custom', component: ImageUploadGallery },
          { name: 'bio', label: 'Biography', type: 'textarea', rows: 4 },
          { name: 'display_order', label: 'Display Order', type: 'number', placeholder: '0' },
          { name: 'is_active', label: 'Active', type: 'checkbox' },
        ]}
        defaultValues={{
          ...defaultValues,
        }}
        transformBeforeSave={(payload) => {
          if (!boardRoles.length) {
            throw new Error('Add or activate a board role in Team Roles before creating board members.');
          }
          const roleId = payload.role_id || null;
          if (!roleId || !boardRoles.some((role) => role.id === roleId)) {
            throw new Error('Please select a valid board role.');
          }
          const images = Array.isArray(payload.images) ? payload.images : [];
          const featuredImage = images.length > 0 ? images[0] : '';
          const displayOrder = Number(payload.display_order);

          return {
            ...payload,
            role_id: roleId,
            display_order: Number.isFinite(displayOrder) ? displayOrder : 0,
            is_active: Boolean(payload.is_active),
            image: featuredImage,
            images,
          };
        }}
        handleEdit={(record) => {
          // Ensure images is an array for existing records
          const images = record.images
            ? (Array.isArray(record.images) ? record.images : [record.images])
            : (record.image ? [record.image] : []);

          return {
            ...record,
            images,
          };
        }}
        extraButtons={[
          { label: 'Export CSV', onClick: exportToCSV, icon: Download },
          { label: uploading ? 'Uploading...' : 'Bulk Upload', onClick: () => fileInputRef.current.click(), disabled: uploading, icon: Upload },
        ]}
        externalRecords={filteredRecords} // Use filtered records instead of all records
        onSaveSuccess={refresh} // Refresh to get latest data
        onDeleteSuccess={refresh} // Refresh to get latest data
      />
    </>
  );
};

export default BoardMembersModule;
